import {
  Card,
  CardContent,
} from "../../../../../../shared/components/card/Card";
import ObjectiveTrackerCollapseButton from "./ObjectiveTrackerCollapseButton";
import { useTranslation } from "react-i18next";
import {
  catchFunction,
  formatToEurosCurrency,
  formatToTwoDecimalPlaces,
} from "../../../../../../shared/lib/utils";
import { useState } from "react";
import useAxios from "../../../../../../hooks/useAxios";
import useLoading from "../../../../../../shared/components/loading/useLoading";
import {
  OBJECTIVE_TRACKER_DUPLICATE_SUBGOAL,
  OBJECTIVE_TRACKER_NESTED_SUBGOALS,
  OBJECTIVE_TRACKER_SUBGOALS,
} from "../../../../../../shared/lib/apiEndpointsResolver";
import CreateSubgoalDialog from "../create-subgoal/CreateSubgoalDialog";
import useToastWrapper from "../../../../../../hooks/useToastWrapper";
import EditSubgoalDialog from "../edit-subgoal/EditSubgoalDialog";
import ObjectivesTrackerDuplicateButton from "./ObjectivesTrackerDuplicateButton";
import useConfirmDialog from "../../../../../../shared/components/confirm-dialog/useConfirmDialog";
import ObjectivesTrackerArrowIndicator from "./ObjectivesTrackerArrowIndicator";
import ObjectivesTrackerDeleteButton from "./ObjectivesTrackerDeleteButton";

const ObjectivesTrackerSubgoal = ({
  level,
  isCurrent,
  trackerId,
  periodId,
  subgoal,
  selectables,
  removeSubgoalFromParent,
  updateSubgoalFromParent,
  addSubgoalToParent,
}) => {
  const { t } = useTranslation();
  const axios = useAxios();
  const { setLoading } = useLoading();
  const [subgoals, setSubgoals] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isFetched, setIsFetched] = useState(false);
  const toast = useToastWrapper();
  const deleteSubgoalDialog = useConfirmDialog();

  const loadNestedSubgoals = () => {
    if (!isOpen) {
      if (subgoals.length === 0) {
        setLoading(true);
        axios
          .get(
            OBJECTIVE_TRACKER_NESTED_SUBGOALS(trackerId, periodId, subgoal.id),
          )
          .then((response) => {
            setSubgoals(response.data);
          })
          .catch(catchFunction)
          .finally(() => {
            setLoading(false);
            setIsOpen(true);
            setIsFetched(true);
          });
      } else {
        setIsOpen(true);
      }
    } else {
      setIsOpen(false);
    }
  };

  const onSubgoalCreation = (formValues) => {
    setLoading(true);
    const request = {
      parentSubgoalId: subgoal?.id,
      ratio: +formValues.ratio * 0.01,
      dimension: {
        id: formValues.dimensionId,
        name: formValues.dimensionName,
        label: formValues.dimensionLabel,
        value: formValues.dimensionValue,
      },
    };
    axios
      .post(OBJECTIVE_TRACKER_SUBGOALS(trackerId, periodId), request)
      .then((response) => {
        setIsOpen(true);
        setSubgoals([...subgoals, response.data]);
        toast.success(
          t("objectivesTracker.createSubgoalForm.success", {
            name: `${response.data.dimension.label}: ${response.data.dimension.value}`,
          }),
        );
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const onSubgoalDeletionClickHandler = () => {
    deleteSubgoalDialog.display(
      t("objectivesTracker.deleteSubgoalConfirmation", {
        name: `${subgoal?.dimension?.label}: ${subgoal?.dimension?.value}`,
      }),
    );
  };

  const onSubgoalDeletion = () => {
    setLoading(true);
    axios
      .delete(
        OBJECTIVE_TRACKER_NESTED_SUBGOALS(trackerId, periodId, subgoal?.id),
      )
      .then((_) => {
        removeSubgoalFromParent(subgoal.id);
        toast.success(
          t(
            "objectivesTracker.createSubgoalForm.deleteSubgoal",
            `${subgoal?.dimension?.label}: ${subgoal?.dimension?.value}`,
          ),
        );
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const removeSubgoal = (subgoalId) => {
    const subgoalsCopy = [...subgoals];
    const result = subgoalsCopy.filter((s) => s.id !== subgoalId);
    setSubgoals(result);
  };

  const onSubgoalEdition = (formValues) => {
    setLoading(true);
    const request = {
      parentSubgoalId: subgoal?.id,
      ratio: +formValues.ratio * 0.01,
      dimension: {
        id: formValues.dimensionId,
        name: formValues.dimensionName,
        label: formValues.dimensionLabel,
        value: formValues.dimensionValue,
      },
    };
    axios
      .put(
        OBJECTIVE_TRACKER_NESTED_SUBGOALS(trackerId, periodId, subgoal?.id),
        request,
      )
      .then((response) => {
        updateSubgoalFromParent(response.data);
        setIsOpen(false);
        setSubgoals([]);
        setIsFetched(false);
        toast.success(
          t("objectivesTracker.editSubgoalForm.success", {
            name: `${response.data.dimension.label}: ${response.data.dimension.value}`,
          }),
        );
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const updateSubgoal = (subgoal) => {
    const subgoalsCopy = [...subgoals];
    const index = subgoals.findIndex((s) => s.id === subgoal.id);
    subgoalsCopy[index] = subgoal;
    setSubgoals(subgoalsCopy);
  };

  const onSubgoalDuplication = () => {
    setLoading(true);
    axios
      .post(
        OBJECTIVE_TRACKER_DUPLICATE_SUBGOAL(trackerId, periodId, subgoal.id),
      )
      .then((response) => {
        addSubgoalToParent(response.data);
        toast.success(
          t("objectivesTracker.duplicateSubgoal", {
            name: `${response.data.dimension.label}: ${response.data.dimension.value}`,
          }),
        );
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const addSubgoal = (subgoal) => {
    setSubgoals([...subgoals, subgoal]);
  };

  return (
    <>
      {deleteSubgoalDialog.renderDialog(onSubgoalDeletion)}
      <Card style={{ marginLeft: `${level * 20}px` }}>
        <CardContent className="p-1">
          <div className="flex flex-row justify-between items-center">
            <ObjectiveTrackerCollapseButton
              isDisabled={isFetched && subgoals.length === 0}
              onClickHandler={loadNestedSubgoals}
            />
            <div>
              {`${subgoal?.dimension?.label}: ${
                subgoal?.dimension?.value
              } (${formatToTwoDecimalPlaces(subgoal?.ratio * 100)}%)`}
            </div>
            <div>
              {t("objectivesTracker.detail.objectiveLabel")}:{" "}
              {formatToEurosCurrency(subgoal?.result?.goal)}
            </div>
            <div>
              {t("objectivesTracker.detail.achievedLabel")} :{" "}
              {formatToEurosCurrency(subgoal?.result?.achieved)}
            </div>
            <div>
              {t("objectivesTracker.detail.expectedLabel")} :{" "}
              {formatToEurosCurrency(subgoal?.result?.expected)}
            </div>
            <div className="flex flex-row gap-x-1">
              {t("objectivesTracker.detail.differenceLabel")}:{" "}
              {formatToEurosCurrency(subgoal?.result?.difference)}
              <ObjectivesTrackerArrowIndicator
                difference={subgoal?.result?.difference}
              />
            </div>
            {isCurrent ? (
              <div>
                {t("objectivesTracker.detail.requiredDailySpendLabel")} :{" "}
                {formatToEurosCurrency(subgoal?.result?.requiredDailySpend)}
              </div>
            ) : (
              <></>
            )}
            <div className="flex flex-row gap-x-1">
              <CreateSubgoalDialog
                onSubmitHandler={onSubgoalCreation}
                parentGoal={subgoal?.result?.goal}
                selectables={selectables}
              />
              <ObjectivesTrackerDuplicateButton
                onClickHandler={onSubgoalDuplication}
              />
              <EditSubgoalDialog
                onSubmitHandler={onSubgoalEdition}
                selectables={selectables}
                subgoal={subgoal}
              />
              <ObjectivesTrackerDeleteButton
                onDeleteHandler={onSubgoalDeletionClickHandler}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      {isOpen &&
        subgoals &&
        subgoals.map((innerSubgoal) => (
          <ObjectivesTrackerSubgoal
            key={innerSubgoal.id}
            level={level + 1}
            isCurrent={isCurrent}
            trackerId={trackerId}
            periodId={periodId}
            subgoal={innerSubgoal}
            selectables={selectables}
            removeSubgoalFromParent={removeSubgoal}
            updateSubgoalFromParent={updateSubgoal}
            addSubgoalToParent={addSubgoal}
          />
        ))}
    </>
  );
};

export default ObjectivesTrackerSubgoal;
